import React, {useState, useEffect, useRef} from 'react';
import LoginModalContent from './LoginModalContent';
import CancelModalContent from './CancelModalContent/index';
import Forms from './RegisterFlow/Forms';
import Button from "@apps/Shared/Components/Button";
import {ButtonType} from '@sharedComponents/Button/types';
import Fullscreen from "@apps/Shared/Components/Fullscreen";
import Modal from "@apps/Shared/Components/Modal";
import {isBrowser} from "@apps/Shared/Utils";
import BrowserStorage, {StorageType} from "@frontendUtils/browser-storage";
import {CompanyRegistrationsBlockProps} from "@apps/CompanyRegistrations/types";
import {GetUserSessionContext} from "@apps/Shared/Utils/ContextHelper";
import {getLocale} from "@apps/Shared/Utils/Utils";
import {useGetMyPagesContainerPageUrlQuery} from "@apps/Shared/GraphQL/generated";
import {B2C} from "@apps/Shared/Services/Axios/Api";
import i18n from "@apps/Shared/translation/i18n";
import './Scss/company-registrations-block.scss'

const CompanyRegistrationsBlock = (props: CompanyRegistrationsBlockProps) => {
    const getIsLoggedIn = () => !!GetUserSessionContext()?.User?.id;
    const [isLoggedIn, setIsLoggedIn] = useState(getIsLoggedIn);

    const [showFullscreen, setShowFullscreen] = useState(false)
    const [showModalFlow, setShowModalFlow] = useState<boolean>(false)
    const [showLoginModal, setShowLoginModal] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState<boolean>(false)

    // To return focus to when cancel modal is "canceled"
    const lastFocusedElement = useRef<HTMLElement | null>(null);

    const locale = getLocale();

    const {data} = useGetMyPagesContainerPageUrlQuery({
        locale: [locale]
    });

    const myPagesRoute = data?.MyPagesContainerPageModel?.items?.length ? data?.MyPagesContainerPageModel?.items[0]?.RelativePath : '';

    const sessionStorage = isBrowser && new BrowserStorage(StorageType.SESSION)

    const formSpec = Forms[props.form as keyof typeof Forms || ''] || {}
    const loginUrl = `${B2C.URL}/signin?lang={{lang}}&returnUrl=${myPagesRoute}`

    const onHideFullscreen = () => {
        setShowFullscreen(false)
    }

    const onHideModalFlow = () => {
        setShowModalFlow(false)
    }

    const shouldOpenAfterLogin = () => {
        try {
            const openRegistration = (
              sessionStorage &&
              sessionStorage.getItem('CRBOpen') === 'true' &&
              sessionStorage.getItem('CRBForm') === props.form &&
              isLoggedIn
            )
            if (openRegistration && formSpec.fullscreen) {
                setShowFullscreen(true)
                onHideModalFlow()
                sessionStorage && sessionStorage.removeItem('CRBOpen');
                sessionStorage && sessionStorage.removeItem('CRBForm');
            } else if (openRegistration && !formSpec.fullscreen) {
                setShowModalFlow(true)
                onHideFullscreen()
                sessionStorage && sessionStorage.removeItem('CRBOpen');
                sessionStorage && sessionStorage.removeItem('CRBForm');
            } else {
                onHideFullscreen()
                onHideModalFlow()
            }
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        shouldOpenAfterLogin();
    }, [isLoggedIn]);

    useEffect(() => {
        if (
            !sessionStorage ||
            sessionStorage.getItem('CRBOpen') !== 'true' ||
            sessionStorage.getItem('CRBForm') !== props.form ||
            isLoggedIn
        ) {
            return;
        }

        const loginCheck = window.setInterval(() => {
            if (getIsLoggedIn()) {
                window.clearInterval(loginCheck);
                setIsLoggedIn(true);
            }
        }, 100);

        return () => window.clearInterval(loginCheck);
    }, []);

    const onRegisterNew = () => {
        if (isLoggedIn) {
            if (formSpec.fullscreen) {
                setShowFullscreen(true)
            } else {
                setShowModalFlow(true)
            }
        } else {
            setShowLoginModal(true)
        }
    }

    const setSessionStorage = () => {
        if (sessionStorage) {
            sessionStorage.setItem(
                'loginRedirectUrl',
                `${window.location.href}`
            )

            sessionStorage.setItem('CRBOpen', 'true')
            sessionStorage.setItem('CRBForm', props.form ?? '');
        }
    }

    const onRedirectToLogin = () => {
        setShowLoginModal(false);
        setSessionStorage();
        window.location.href = loginUrl.replace('{{lang}}', i18n.language);
    }

    const onHideCancelModal = () => {
        setShowCancelModal(false)

        requestAnimationFrame(() => {
            lastFocusedElement.current?.focus()
        })
    }

    const onConfirmCancel = () => {
        onHideCancelModal()
        onHideFullscreen()
    }

    const onCancelRegistration = () => {
        // Store trigger element to be returned to if cancel modal is canceled. "Don't cancel / Stay".
        lastFocusedElement.current = document.activeElement as HTMLElement

        setShowCancelModal(true)
    }

    const cmsContent = {
        step1: props.extra1,
        step2: props.extra2,
        step3: props.extra3,
        step4: props.extra4,
        step5: props.extra5,
    }

    const onShownFullscreen = () => {
        sessionStorage && sessionStorage.removeItem('CRBOpen')
        sessionStorage && sessionStorage.removeItem('CRBForm')
    }

    const getFormComponent = () => {
        const FormComponent = formSpec.component

        if (!FormComponent) {
            console.warn('[CRB]:: Missing form')
            return ''
        }

        const open = formSpec.fullscreen ? showFullscreen : showModalFlow
        const onHide = formSpec.fullscreen ? onHideFullscreen : onHideModalFlow

        return (
            <FormComponent
                myPagesRoute={myPagesRoute ?? ''}
                helpText={props.helpText}
                cmsContent={cmsContent}
                termsAndConditions={
                    props.customTermsAndConditions || props.termsAndConditions
                }
                termsAndConditionsButtons={props.termsAndConditionsButtons}
                alternativeTermsAndConditions={
                    props.alternativeTermsAndConditions
                }
                onCancel={onCancelRegistration}
                open={open}
                onHide={onHide}
                formType={props.form}
            />
        )
    }

    return (
        <>
            {isBrowser ? (
                <div className='company-registrations-block'>
                    <Button
                        type={props.buttonType || ButtonType.PRIMARY}
                        text={props.buttonText}
                        onClick={onRegisterNew}
                        className={`new-company-button ${props.form}`}
                    />
                    {formSpec.fullscreen ? (
                        <Fullscreen
                            open={showFullscreen}
                            onShown={onShownFullscreen}
                        >
                            {showFullscreen && props.form && getFormComponent()}
                        </Fullscreen>
                    ) : (
                        <>{getFormComponent()}</>
                    )}
                    <Modal
                        open={showCancelModal}
                        size="xs"
                        handleClose={onHideCancelModal}
                    >
                        <CancelModalContent
                            onConfirmCancel={onConfirmCancel}
                            onHideCancelModal={onHideCancelModal}
                        />
                    </Modal>
                    <Modal
                        open={showLoginModal}
                        handleClose={() => setShowLoginModal(false)}
                    >
                        <LoginModalContent handleLogin={onRedirectToLogin}/>
                    </Modal>
                </div>
            ) : (
                ''
            )}
        </>
    )
}

export default CompanyRegistrationsBlock
